import React, { useState, useEffect } from 'react';
import { TextField, InputAdornment, SxProps, styled, Stack, Button, IconButton, TextFieldProps } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { componentColors } from '../utils/color';

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  minWidth: '250px',
  ...(theme.palette.mode === 'dark' && {
    backgroundColor: theme.palette.background.paper,
  }),
}));

export interface GlobalSearchProps {
  id: string;
  label: string;
  onChange: (val: string) => void;
  filterState: string | null;
  sx?: SxProps;
  autocomplete?: string;
  ignoreFs?: boolean;
  hideActionButton?: boolean;
  size?: TextFieldProps['size'];
  auto?: boolean;
  debounceTime?: number;
}

const GlobalSearch = ({
  id,
  label,
  onChange,
  filterState = '',
  sx,
  autocomplete,
  ignoreFs = false,
}: GlobalSearchProps) => {
  // Using debounce
  const [inputVal, setInputVal] = useState<string>(filterState || '');
  const delay = 200;
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (filterState !== inputVal) onChange(inputVal);
    }, delay);
    return () => clearTimeout(timeoutId);
  }, [inputVal]);

  useEffect(() => {
    if ((filterState || '') !== inputVal) {
      setInputVal(filterState || '');
    }
  }, [filterState]);

  // Default to never shrinking label
  const [shrink, setShrink] = useState(false);

  // Shrink Label on Fucus
  const handleFocus = () => {
    setShrink(true);
  };

  // "Unshrink" label on blur if the field is empty
  const handleBlur = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.target.value.trim().length === 0) setShrink(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputVal(event.target.value);
  };

  useEffect(() => {
    ((filterState as string) || '').trim().length > 0 && setShrink(true);
  }, [filterState, shrink]);

  return (
    <StyledTextField
      sx={[
        { '& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-focused': { color: (theme) => theme.palette.info.main } },
        ...(shrink ? [] : [{ '& .MuiInputLabel-root': { transform: 'translate(44px, 20px) scale(1)' } }]),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      fullWidth={true}
      id={id}
      label={label}
      variant="outlined"
      onFocus={handleFocus}
      onBlur={handleBlur}
      value={inputVal}
      onChange={handleChange}
      autoComplete={autocomplete}
      className={ignoreFs ? 'fs-exclude' : undefined}
      slotProps={{
        input: {
          type: 'search',
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.primary' }} />
            </InputAdornment>
          ),
          inputProps: {
            max: 500,
          },
        },
        inputLabel: { shrink: shrink },
      }}
    />
  );
};

const GlobalSearchV2 = ({
  id,
  label,
  onChange,
  filterState = '',
  sx,
  hideActionButton = false,
  size = 'small',
  auto = false,
  debounceTime = 500,
}: GlobalSearchProps) => {
  const [inputVal, setInputVal] = useState<string>(filterState || '');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!auto) return;

    const timeoutId = setTimeout(() => {
      if (filterState !== inputVal) onChange(inputVal);
    }, debounceTime);
    return () => clearTimeout(timeoutId);
  }, [inputVal]);

  // Reset input when global state is null
  useEffect(() => {
    if (!filterState && !!inputVal) setInputVal('');
  }, [filterState]);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);
  const handleSearch = () => onChange(inputVal);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputVal(event.target.value);
    if (event.target.value === '') onChange('');
  };

  const handleClear = () => {
    setInputVal('');
    onChange('');
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSearch();
  };

  const shrink = isFocused || inputVal !== '';

  const inputHeight = size === 'small' ? '42px' : '56px';
  const lineHeight = size === 'small' ? '23px' : '35px';

  return (
    <form onSubmit={handleSubmit}>
      <Stack direction="row">
        <TextField
          id={id}
          variant="outlined"
          label={label}
          value={inputVal}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          size={size}
          fullWidth
          sx={[
            { backgroundColor: 'background.paper' },
            { '& .MuiInputBase-root': { borderTopRightRadius: 0, borderBottomRightRadius: 0, height: inputHeight } },
            { '& .MuiInputLabel-root': { lineHeight: lineHeight } },
            { '& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-focused': { color: (theme) => theme.palette.info.main } },
            { '& .MuiInputLabel-root.MuiInputLabel-shrink': { lineHeight: '18px' } },
            { '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': { transform: 'translate(46px, 10px) scale(1)' } },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.primary' }} />
                </InputAdornment>
              ),
              endAdornment: inputVal ? (
                <InputAdornment position="end" sx={{ mr: -1 }}>
                  <IconButton onClick={handleClear}>
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
            inputLabel: { shrink },
          }}
        />
        {!hideActionButton && (
          <Button
            variant="outlined"
            className="button"
            size="large"
            sx={{
              lineHeight: '24px',
              borderLeft: 'none',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderColor: (theme) => componentColors(theme.palette.primary).outlinedBorder,
            }}
            type="submit"
          >
            Search
          </Button>
        )}
      </Stack>
    </form>
  );
};

export { GlobalSearch, GlobalSearchV2 };
